import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ApiError, api } from "@/lib/api";
import { todayKey } from "@/lib/date";
import type { RelayItem, RelaySession } from "@/lib/types";
import { navigate } from "@/lib/router";
import { PROFILES, useProfileStore } from "@/state/profile";
import {
  cacheGet,
  cacheSet,
  enqueue,
  relayCacheKey,
  type CachedRelay,
} from "@/lib/offline";
import { queueChanged, useSyncStore } from "@/state/sync";
import { SpeakButton } from "@/components/SpeakButton";
import { bigCelebration, burstConfetti, sfxCorrect, sfxWrong } from "@/lib/rewards";
import { cn } from "@/lib/cn";

type Stage = "loading" | "intro" | "playing" | "handoff" | "result" | "error";

export function RelayPage() {
  const profileId = useProfileStore((state) => state.activeId);
  const setActive = useProfileStore((state) => state.setActive);
  const [relay, setRelay] = useState<RelaySession | null>(null);
  const [stage, setStage] = useState<Stage>("loading");
  const [index, setIndex] = useState(0);
  const [attempt, setAttempt] = useState<0 | 1>(0);
  const [feedback, setFeedback] = useState<null | { correct: boolean; answer: string }>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStage("loading");
    const date = todayKey();
    api.relay.today(profileId, date)
      .then(async (value) => {
        await cacheSet(relayCacheKey(profileId, date), value);
        if (cancelled) return;
        applyLoadedRelay(value, setRelay, setStage, setIndex, setAttempt);
      })
      .catch(async (reason) => {
        const cached = await cacheGet<CachedRelay>(relayCacheKey(profileId, date)).catch(() => null);
        if (cancelled) return;
        if (cached) {
          applyLoadedRelay(cached, setRelay, setStage, setIndex, setAttempt);
          setError("Sin conexión · el turno se guardará en este teléfono");
        } else {
          setError(reason instanceof Error ? reason.message : "No se pudo abrir el relevo");
          setStage("error");
        }
      });
    return () => { cancelled = true; };
  }, [profileId]);

  async function submit(userAnswer: string) {
    if (!relay || feedback) return;
    const item = relay.items[index];
    if (!item) return;
    const body = {
      date_key: relay.date_key,
      request_id: crypto.randomUUID(),
      item_index: index,
      attempt,
      user_answer: userAnswer,
    } as const;
    const localCorrect = matches(userAnswer, item.answers_es);
    let result = { correct: localCorrect, correct_answer: item.answers_es[0] ?? "" };
    try {
      result = await api.relay.answer(profileId, body);
    } catch (reason) {
      const retryable = !(reason instanceof ApiError) || reason.status >= 500;
      if (!retryable) {
        setError(reason instanceof Error ? reason.message : "Respuesta rechazada");
        return;
      }
      await enqueue({
        id: body.request_id,
        kind: "relay_answer",
        profileId,
        body,
        createdAt: Date.now(),
      });
      await queueChanged();
      setError("Guardado en el teléfono · se sincronizará");
    }
    if (result.correct) {
      sfxCorrect(index + 1);
      burstConfetti(PROFILES[profileId].color);
    } else {
      sfxWrong();
    }
    setFeedback({ correct: result.correct, answer: result.correct_answer });
  }

  async function next() {
    if (!relay || !feedback) return;
    if (!feedback.correct && attempt === 0) {
      setAttempt(1);
      setFeedback(null);
      return;
    }
    const nextIndex = index + 1;
    if (nextIndex < relay.items.length) {
      setIndex(nextIndex);
      setAttempt(0);
      setFeedback(null);
      return;
    }
    await finish();
  }

  async function finish() {
    if (!relay) return;
    setFeedback(null);
    try {
      await useSyncStore.getState().flush();
      const completed = await api.relay.complete(profileId, relay.date_key);
      setRelay(completed.relay);
      await cacheSet(relayCacheKey(profileId, relay.date_key), completed.relay);
      if (completed.relay.reveal) {
        setStage("result");
        bigCelebration();
      } else {
        setStage("handoff");
      }
    } catch (reason) {
      const retryable = !(reason instanceof ApiError) || reason.status >= 500;
      if (!retryable) {
        setError(reason instanceof Error ? reason.message : "No se pudo terminar");
        return;
      }
      await enqueue({
        id: `relay-complete:${profileId}:${relay.date_key}`,
        kind: "relay_complete",
        profileId,
        body: { date_key: relay.date_key },
        createdAt: Date.now(),
      });
      await queueChanged();
      setStage("handoff");
      setError("Turno guardado · el resultado aparecerá al sincronizar");
    }
  }

  if (stage === "loading") return <FullState emoji="🤝" title="Preparando el relevo…" />;
  if (stage === "error" || !relay) {
    return <FullState emoji="🙈" title="No se pudo abrir" detail={error ?? undefined} />;
  }
  const opponent = relay.opponent_id;
  if (stage === "intro") {
    return (
      <RelayFrame onBack={() => navigate({ name: "home" })}>
        <div className="flex-1 grid place-items-center text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="chunk p-6 w-full">
            <div className="text-6xl">{relay.theme_emoji}</div>
            <div className="mt-3 pill">Relevo diario · 8 frases</div>
            <h1 className="mt-3 font-display text-3xl">{relay.theme_title}</h1>
            <p className="mt-2 text-sm text-brand-muted font-bold">
              Mismo reto, ayuda ajustada. Tu objetivo es {relay.target}/8.
            </p>
            <p className="mt-3 rounded-2xl bg-brand-saffron/15 px-3 py-2 text-xs text-brand-muted font-bold">
              100% hoy desbloquea menos pistas mañana. Las frases siguen siendo cotidianas.
            </p>
            <div className="mt-5 rounded-2xl bg-brand-canvas p-4">
              <div className="text-xs uppercase tracking-widest text-brand-muted font-black">Turno privado</div>
              <div className="mt-1 font-display text-2xl" style={{ color: PROFILES[profileId].color }}>
                Soy {PROFILES[profileId].name}
              </div>
            </div>
            <button onClick={() => setStage("playing")} className="chunk-btn-primary w-full mt-5">
              Estoy listo
            </button>
          </motion.div>
        </div>
      </RelayFrame>
    );
  }
  if (stage === "handoff") {
    return (
      <RelayFrame onBack={() => navigate({ name: "home" })}>
        <div className="flex-1 grid place-items-center text-center">
          <div className="chunk p-6 w-full">
            <div className="text-6xl">📱</div>
            <h1 className="mt-3 font-display text-3xl">Turno guardado</h1>
            <p className="mt-2 text-sm text-brand-muted font-bold">
              Sin pistas del resultado. Pasa el teléfono a {PROFILES[opponent].name}.
            </p>
            <button
              onClick={() => setActive(opponent)}
              className="chunk-btn-primary w-full mt-5"
              style={{ background: PROFILES[opponent].color }}
            >
              Ya soy {PROFILES[opponent].name}
            </button>
            <button onClick={() => navigate({ name: "home" })} className="chunk-btn-ghost w-full mt-1">
              Volver al inicio
            </button>
            {error && <p className="mt-3 text-xs text-brand-muted font-bold">{error}</p>}
          </div>
        </div>
      </RelayFrame>
    );
  }
  if (stage === "result" && relay.reveal) {
    return <RelayResult relay={relay} onBack={() => navigate({ name: "home" })} />;
  }

  const item = relay.items[index];
  return (
    <RelayFrame onBack={() => navigate({ name: "home" })}>
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-brand-muted">
          <span>{relay.theme_emoji} {relay.theme_title}</span>
          <span>{index + 1}/8</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-brand-border overflow-hidden">
          <div className="h-full bg-brand-violet transition-all" style={{ width: `${(index / 8) * 100}%` }} />
        </div>
      </div>
      {item && (
        <RelayQuestion
          key={`${index}:${attempt}`}
          item={item}
          support={relay.support_level}
          attempt={attempt}
          feedback={feedback}
          onSubmit={submit}
          onNext={next}
        />
      )}
      {error && <div className="mt-2 text-center text-xs text-brand-muted font-bold">{error}</div>}
    </RelayFrame>
  );
}

function RelayQuestion({
  item,
  support,
  attempt,
  feedback,
  onSubmit,
  onNext,
}: {
  item: RelayItem;
  support: RelaySession["support_level"];
  attempt: 0 | 1;
  feedback: null | { correct: boolean; answer: string };
  onSubmit: (value: string) => void;
  onNext: () => void;
}) {
  const [value, setValue] = useState("");
  const guided = support === "guided" || attempt === 1;
  return (
    <main className="flex-1 flex flex-col">
      <div className="flex justify-center mb-2">
        <span className={cn("pill", attempt === 1 && "bg-brand-saffron/20 border-brand-saffron/40 text-brand-ink")}>
          {attempt === 1 ? "Segunda oportunidad" : supportLabel(support)}
        </span>
      </div>
      <div className="chunk p-6 text-center">
        <div className="text-xs uppercase tracking-widest text-brand-muted font-black">¿Cómo lo dirías?</div>
        <div className="mt-3 font-display text-3xl leading-tight">{item.prompt_en}</div>
        {!guided && support === "standard" && (
          <div className="mt-3 text-sm text-brand-muted font-bold">Pista: {mask(item.answers_es[0] ?? "")}</div>
        )}
      </div>
      {!feedback && guided ? (
        <div className="mt-4 grid grid-cols-1 gap-3">
          {item.options.map((option) => (
            <button key={option} onClick={() => onSubmit(option)} className="chunk-btn-secondary min-h-14 text-lg font-display">
              {option}
            </button>
          ))}
        </div>
      ) : !feedback ? (
        <form
          className="mt-5"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (value.trim()) onSubmit(value.trim());
          }}
        >
          <input
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoCapitalize="none"
            autoComplete="off"
            placeholder="En español…"
            className="w-full rounded-chunk bg-brand-surface border-2 border-brand-border px-5 py-5 text-center font-display text-2xl focus:border-brand-violet focus:outline-none"
          />
          <button disabled={!value.trim()} className="chunk-btn-primary w-full mt-3 disabled:opacity-50">Comprobar</button>
        </form>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={cn("chunk mt-5 p-5 text-center", feedback.correct ? "bg-brand-mint/15" : "bg-brand-rose/10")}>
          <div className="text-4xl">{feedback.correct ? "✨" : "🌱"}</div>
          <div className="mt-2 font-display text-2xl">{feedback.correct ? "¡Sí!" : "Casi"}</div>
          <div className="mt-2 font-display text-xl text-brand-mint">{feedback.answer}</div>
          <div className="mt-2 flex justify-center"><SpeakButton text={feedback.answer} /></div>
          <button onClick={onNext} className="chunk-btn-primary w-full mt-4">
            {!feedback.correct && attempt === 0 ? "Probar una vez más" : item.item_index === 7 ? "Terminar turno" : "Siguiente"}
          </button>
        </motion.div>
      )}
    </main>
  );
}

function RelayResult({ relay, onBack }: { relay: RelaySession; onBack: () => void }) {
  const reveal = relay.reveal!;
  const winner = reveal.winner === "tie" ? "¡Empate!" : `Gana ${PROFILES[reveal.winner].name}`;
  const [first, second] = reveal.scores;
  const firstWins = reveal.weekly.wins.find((entry) => entry.profile_id === first.profile_id)?.wins ?? 0;
  const secondWins = reveal.weekly.wins.find((entry) => entry.profile_id === second.profile_id)?.wins ?? 0;
  return (
    <RelayFrame onBack={onBack}>
      <div className="flex-1 text-center py-4">
        <div className="text-6xl">🏆</div>
        <h1 className="mt-2 font-display text-3xl">{winner}</h1>
        <p className="mt-1 text-brand-muted font-bold">Objetivos personales hacen el duelo justo.</p>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Score name={PROFILES[first.profile_id].name} color={PROFILES[first.profile_id].color} score={first.correct} target={first.target} recovered={first.recovered} />
          <Score name={PROFILES[second.profile_id].name} color={PROFILES[second.profile_id].color} score={second.correct} target={second.target} recovered={second.recovered} />
        </div>
        <div className="chunk p-4 mt-4">
          <div className="font-display text-2xl">🔥 {reveal.duo_streak} días juntos</div>
          <div className="mt-1 text-xs uppercase tracking-widest text-brand-muted font-black">
            Esta semana · {PROFILES[first.profile_id].name} {firstWins} — {secondWins} {PROFILES[second.profile_id].name} · {reveal.weekly.ties} empates
          </div>
        </div>
        <button onClick={onBack} className="chunk-btn-primary w-full mt-5">Volver al inicio</button>
      </div>
    </RelayFrame>
  );
}

function Score({ name, color, score, target, recovered }: { name: string; color: string; score: number; target: number; recovered: number }) {
  return (
    <div className="chunk p-4" style={{ borderColor: color }}>
      <div className="font-display text-lg" style={{ color }}>{name}</div>
      <div className="font-display text-4xl">{score}/8</div>
      <div className="text-xs text-brand-muted font-bold">meta {target} · +{recovered} recuperadas</div>
    </div>
  );
}

function RelayFrame({ children, onBack }: { children: ReactNode; onBack: () => void }) {
  return (
    <div className="min-h-[100dvh] safe-pt safe-pb px-4 flex flex-col w-full max-w-lg mx-auto">
      <header className="pb-3">
        <button onClick={onBack} aria-label="Volver" className="h-10 w-10 rounded-2xl grid place-items-center bg-brand-surface border border-brand-border shadow-chunk">←</button>
      </header>
      {children}
    </div>
  );
}

function FullState({ emoji, title, detail }: { emoji: string; title: string; detail?: string }) {
  return (
    <div className="min-h-[100dvh] grid place-items-center text-center p-6">
      <div><div className="text-6xl">{emoji}</div><div className="mt-3 font-display text-2xl">{title}</div>{detail && <p className="mt-2 text-sm text-brand-muted font-bold">{detail}</p>}<button onClick={() => navigate({ name: "home" })} className="chunk-btn-ghost mt-4">Volver</button></div>
    </div>
  );
}

function applyLoadedRelay(
  relay: RelaySession,
  setRelay: (relay: RelaySession) => void,
  setStage: (stage: Stage) => void,
  setIndex: (index: number) => void,
  setAttempt: (attempt: 0 | 1) => void,
) {
  setRelay(relay);
  if (relay.reveal) return setStage("result");
  if (relay.completed) return setStage("handoff");
  const firstMissing = relay.items.findIndex((item) => !relay.attempts.some((entry) => entry.item_index === item.item_index && (entry.correct || entry.attempt === 1)));
  setIndex(firstMissing < 0 ? 0 : firstMissing);
  const first = relay.attempts.find((entry) => entry.item_index === firstMissing && entry.attempt === 0);
  setAttempt(first && !first.correct ? 1 : 0);
  setStage("intro");
}

function supportLabel(support: RelaySession["support_level"]): string {
  return support === "guided" ? "Con opciones" : support === "standard" ? "Con una pista" : "Reto libre";
}

function normalise(value: string): string {
  return value.trim().toLocaleLowerCase("es").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[¿?¡!.,;:]/g, "").replace(/\s+/g, " ");
}

function matches(value: string, answers: string[]): boolean {
  const candidate = normalise(value);
  return answers.some((answer) => normalise(answer) === candidate);
}

function mask(value: string): string {
  return value.split(/(\s+)/).map((part) => /^\s+$/.test(part) ? part : Array.from(part).map((letter, index) => index === 0 ? letter : "_").join("")).join("");
}
